const uuid = require("nanoid")
import { observable, action } from "mobx"
import { Member, $Member } from "./Member"
import { Waiver, $Waiver } from "./Waiver"
import { Session, $Session } from "./Session"
import { SessionGroup } from "./SessionGroup"
import { SessionType, $SessionType } from "./SessionType"
import { Booking, $Booking } from "./Booking"
import { lightDBHost } from "../../light/utils"

export interface LightObject {
  $id: string
  $version: string
  $versionDateString: string
  $parentVersion: string | null
  $type: ObjectTypeString
}

export interface LightModel {
  id: string
  type: ObjectTypeString
  object: LightObject
  children: LightModel[] | null
  isValid: boolean
  invalidMessage: string | undefined
  clean: () => void
  lookupValues?: string[]
}

export interface BookingDBFile {
  objects: {
    [$id: string]: LightObject
  }
  memberLookup: { [id: string]: string }
  sessionLookup: { [id: string]: string }
  sessionTypeLookup: { [id: string]: string }
}

export interface Errors {
  [name: string]: string | boolean
}

export interface LookupRequest {
  lookupKey: string
  type: string
  createIfNotFound?: LightObject
}

export interface LookupResponse extends LookupRequest {
  isOk: boolean
  errorMessage: string | null
  answer: string | null
}

export interface SyncReq {
  file: BookingDBFile
  lookupRequests: LookupRequest[]
}

export interface SyncResponse {
  file: BookingDBFile
  lookupResponses: LookupResponse[]
  generalError: null | string
  objectErrors: { [id: string]: { id: string; err: string } }
}

export function createDBFile(): BookingDBFile {
  return {
    objects: {},
    memberLookup: {},
    sessionLookup: {},
    sessionTypeLookup: {}
  }
}

export type ObjectTypeString =
  | "booking"
  | "waiver"
  | "member"
  | "booking"
  | "sessionGroup"
  | "sessionType"
  | "session"
  | "order"

export type ModelType =
  | Booking
  | Waiver
  | Member
  | SessionGroup
  | SessionType
  | Session

export class ObjectLookupError extends Error {
  constructor(message: string) {
    super(message)
    Object.setPrototypeOf(this, ObjectLookupError.prototype)
  }
}

export class ObjectVerificationError extends Error {
  constructor($id: string, message: string) {
    super(message)
    this.$id = $id
    Object.setPrototypeOf(this, ObjectVerificationError.prototype)
  }
  $id: string
}

export class BookingDB {
  constructor(isClient = false, file = createDBFile()) {
    this.file = isClient ? observable(file) : file
    this.isClient = isClient
  }

  file: BookingDBFile

  isClient: boolean = false

  get isServer() {
    return !this.isClient
  }

  createSyncRequest() {
    const dbSync: SyncReq = {
      file: createDBFile(),
      lookupRequests: []
    }
    return dbSync
  }

  createSyncResponse() {
    const dbSync: SyncResponse = {
      file: createDBFile(),
      lookupResponses: [],
      objectErrors: {},
      generalError: null
    }
    return dbSync
  }

  currentStateOfFile(file: BookingDBFile): BookingDBFile {
    const found = createDBFile()
    Object.keys(file.objects).forEach(key => {
      found[key] = this.file.objects[key]
    })
    return found
  }

  @action.bound
  verifyObject(object: LightObject) {
    const existing = this.file.objects[object.$id]
    // if (existing && existing.$id === object.$id) {
    //   console.warn(`Saving same object version twice ${object.$id}`)
    //   console.warn(JSON.stringify(existing))
    // }

    if (
      existing &&
      existing.$id !== object.$id &&
      (!object.$parentVersion || object.$parentVersion != existing.$version)
    ) {
      const message = `Existing object ${existing.$id} is not parent of ${
        object.$id
      }`
      console.error(JSON.stringify(object))
      const err = new ObjectVerificationError(object.$id, message)
      throw err
    }
  }

  save(model: LightModel, seen?: { [index: string]: string }) {
    const { children } = model

    seen = seen ? seen : {}

    model.clean()
    if (!model.isValid) {
      // console.error(JSON.stringify(model.object))
      // console.error(model.invalidMessage)
      throw `Model invalid: ${model.invalidMessage}`
    }
    this.versionObject(model.object)
    this.file.objects[model.object.$id] = model.object
    this.saveLookups(model)

    if (this.isClient) {
      this.nextSyncRequest.file.objects[model.object.$id] = model.object
    }
    children && children.forEach(child => this.save(child, seen))
  }

  versionObject(object: LightObject) {
    object.$parentVersion = object.$version
    object.$version = uuid()
    object.$versionDateString = new Date().toISOString()
  }

  @action.bound
  saveLookups(model: LightModel) {
    const object = model.object
    const type = object.$type
    if (model.lookupValues) {
      model.lookupValues.forEach(v => {
        const lookupMapKey = `${type}Lookup`
        const existing = this.file[lookupMapKey][v]
        if (existing && existing != object.$id) {
          throw new ObjectLookupError(
            `Lookup already exists for model of type ${model.type} and id: ${
              model.id
            }`
          )
        } else this.file[lookupMapKey][v] = object.$id
      })
    }
  }

  @action.bound
  verifyAndWriteFile = (file: BookingDBFile) => {
    if (!file || !file.objects) {
      throw new Error("Missing or empty objects")
    }

    Object.values(file.objects).forEach(this.verifyObject)
    Object.values(file.objects).forEach(o => {
      this.file.objects[o.$id] = o
    })

    Object.values(file.objects).forEach(o => {
      const m = this.createLightModel(o)
      if (m) this.saveLookups(m)
      // if (m && !m.isValid) {
      //   throw new ObjectVerificationError(
      //     o.$id,
      //     m.invalidMessage || "Invalid object"
      //   )
      // }
    })
  }

  @action.bound
  writeFile(file: BookingDBFile) {
    Object.keys(file).forEach(key => {
      const subObject = file[key]
      Object.keys(subObject).forEach(k => {
        this.file[key][k] = subObject[k]
      })
    })
  }

  createLightModel(object: LightObject) {
    let model: LightModel | undefined
    const type = object.$type
    if (type === "member") {
      model = new Member(this, object as $Member)
      ;(model as Member).shouldCheckAddress = false
    }
    if (type === "waiver") model = new Waiver(this, object as $Waiver)
    if (type === "session") model = new Session(this, object as $Session)
    if (type === "booking") model = new Booking(this, object as $Booking)
    if (type === "sessionType")
      model = new SessionType(this, object as $SessionType)
    return model
  }

  processSyncRequest(params: SyncReq): SyncResponse {
    const { file, lookupRequests } = params

    const res = this.createSyncResponse()

    try {
      this.verifyAndWriteFile(file)
    } catch (err) {
      if (err instanceof ObjectVerificationError) {
        res.objectErrors[err.$id] = { id: err.$id, err: err.message }
      } else {
        console.error(err)
        res.generalError = JSON.stringify(err)
      }
    }

    if (lookupRequests) {
      lookupRequests.forEach(l => {
        res.lookupResponses.push(this.processLookupRequest(l, res.file))
      })
    }

    return res
  }

  @action.bound
  processSyncResponse(syncReq: SyncReq, syncRes: SyncResponse): void {
    const { file } = syncRes
    try {
      this.verifyAndWriteFile(file)
    } catch (err) {
      console.error("Error processing sync response")
      console.error(err)
      console.error(syncReq)
    }
  }

  dbTypes: string[] = [
    "member",
    "booking",
    "waiver",
    "session",
    "sessionGroup",
    "sessionType"
  ]

  addObjectToFile(
    object: LightObject,
    file: BookingDBFile,
    seen?: { [index: string]: string }
  ) {
    file.objects[object.$id] = object

    seen = seen ? seen : {}
    Object.keys(object).forEach(objectKey => {
      this.dbTypes.forEach(type => {
        if (objectKey.indexOf(`$${type}`) !== 0) return
        const targetKey = objectKey.substring(1)
        const targetMap = `objects`
        const toExpand = object[objectKey]
        if (!toExpand) return
        if (typeof toExpand === "object") {
          let target = file.objects
          if (!target) {
            target = {}
            file[targetKey] = target
          }
          Object.keys(toExpand).forEach(k => {
            if (target[k]) return
            const m = this.file[targetMap][k]
            if (m) {
              target[k] = m
              if (seen && !seen[m.$id]) {
                seen[m.$id] = m.$id
                this.addObjectToFile(m, file, seen)
              }
            } else {
              throw new Error(`Did not find object in ${type} with key ${k}`)
            }
          })
        } else if (!object[targetKey] && typeof toExpand === "string") {
          const m = this.file[targetMap][toExpand]
          if (m) {
            if (seen && !seen[m.$id]) {
              seen[m.$id] = m.$id
              this.addObjectToFile(m, file, seen)
            }
          } else {
            throw new Error(
              `Missing object: Could not find object with type "${type}" and key ${toExpand}.`
            )
          }
        } else if (Array.isArray(toExpand)) {
        }
      })
    })
  }

  @observable
  nextSyncRequest = this.createSyncRequest()

  lookupLocal(lookup: LookupRequest): LightObject | null {
    const { lookupKey, type } = lookup

    if (!lookupKey) {
      throw new Error("Missing lookup key on lookup")
    }

    const lookupMapKey = `${type}Lookup`
    const objectId = this.file[lookupMapKey][lookupKey]
    return objectId ? this.file.objects[objectId] : null
  }

  processLookupRequest(
    lookup: LookupRequest,
    file: BookingDBFile
  ): LookupResponse {
    const { lookupKey, type } = lookup

    if (!lookupKey) {
      throw new Error("Missing lookup key on lookup")
    }

    const lookupMapKey = `${type}Lookup`
    const objectId = this.file[lookupMapKey][lookupKey]
    const object = objectId ? this.file.objects[objectId] : null

    const lookupResponse: LookupResponse = {
      lookupKey: lookup.lookupKey,
      type: lookup.type,
      answer: null,
      isOk: false,
      errorMessage: null
    }

    try {
      if (object) {
        lookupResponse.answer = object.$id
        this.addObjectToFile(object, file)
        const lookupMapKey = `${type}Lookup`
        if (!file[lookupMapKey]) file[lookupMapKey] = {}
        file[lookupMapKey][lookupKey] = lookupResponse.answer
        lookupResponse.isOk = true
      } else if (lookup.createIfNotFound) {
        this.verifyObject(lookup.createIfNotFound)
        const lookupMapKey = `${type}Lookup`
        this.file[lookupMapKey][lookupKey] = lookup.createIfNotFound.$id
        this.addObjectToFile(lookup.createIfNotFound, this.file)
        this.addObjectToFile(lookup.createIfNotFound, file)
        lookupResponse.answer = lookup.createIfNotFound.$id
        lookupResponse.isOk = true
      }
    } catch (err) {
      lookupResponse.isOk = false
      lookupResponse.errorMessage = err.message
    }

    return lookupResponse
  }

  async postNextSyncRequest() {
    const nextSyncRequest = this.nextSyncRequest
    this.nextSyncRequest = this.createSyncRequest()
    return await this.postSyncRequest(nextSyncRequest)
  }

  @action.bound
  async postSyncRequest(syncReq: SyncReq) {
    const res = await fetch(`${lightDBHost()}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      mode: "cors",
      body: JSON.stringify(syncReq)
    })
    const syncRes: SyncResponse = await res.json()
    this.processSyncResponse(syncReq, syncRes)
    return syncRes
  }
}
